import '@apps/web/features/learn_phoebe/ui/learn_phoebe.css';

import { ArrowUpRight, ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@apps/web/components/ui/collapsible';
import { useUI } from '@apps/web/context/ui_provider';
import {
    FAQS,
    LEARN_TASKS,
    type LearnTask,
    PRODUCT_DOCS_URL,
    promptPlainText,
} from '@apps/web/features/learn_phoebe/model/learn_content';
import { useCanTryInChat } from '@apps/web/features/learn_phoebe/model/use_can_try_in_chat';
import {
    useLearnPhoebeAnalytics,
    useLearnPhoebePageViewed,
} from '@apps/web/features/learn_phoebe/model/use_learn_phoebe_analytics';
import { CopyPromptAction } from '@apps/web/features/learn_phoebe/ui/copy_prompt_action';
import { FakeChat } from '@apps/web/features/learn_phoebe/ui/fake_chat';
import { cn } from '@apps/web/lib/utils';

export function LearnPhoebePage() {
    useLearnPhoebePageViewed();
    return (
        <div className="flex h-full min-w-0 flex-col overflow-hidden">
            <div className="px-4 py-4 sm:px-6">
                <div className="mx-auto w-full max-w-5xl">
                    <h1 className="mb-1 text-xl font-semibold sm:text-2xl">
                        Learn Phoebe
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Ask Phoebe like a teammate, not a search box.
                    </p>
                </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:p-6">
                <div className="mx-auto w-full max-w-5xl min-w-0 space-y-8 pb-8">
                    <TaskPlayground />
                    <FaqSection />
                </div>
            </div>
        </div>
    );
}

// Playground layout: an accordion of tasks on the left (the open one shows its
// tip), and a fake read-only chat on the right that plays the open task's
// conversation.
function TaskPlayground() {
    // Everything starts closed, like the FAQ; the chat idles until a task is
    // picked, and clicking the open task closes it again.
    const [activeTaskId, setActiveTaskId] = useState<LearnTask['id'] | null>(
        null,
    );
    const [playbackRun, setPlaybackRun] = useState(0);
    const chatPanelRef = useRef<HTMLDivElement | null>(null);
    const { openChatWithComposerText } = useUI();
    const canTryInChat = useCanTryInChat();
    const { captureTaskOpened, captureTryInChatClicked } =
        useLearnPhoebeAnalytics();
    const activeTask =
        LEARN_TASKS.find((task) => task.id === activeTaskId) ?? null;

    function selectTask(taskId: LearnTask['id']) {
        const isOpening = taskId !== activeTaskId;
        setActiveTaskId((current) => (current === taskId ? null : taskId));
        setPlaybackRun((run) => run + 1);
        // Only an open counts: re-clicking the open task closes it.
        if (isOpening) {
            captureTaskOpened({ surface: 'page', taskId, stepId: null });
        }
        // On stacked layouts the chat sits below the list; bring it into
        // view so the playback isn't happening off screen.
        if (
            isOpening &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 1023px)').matches
        ) {
            requestAnimationFrame(() => {
                chatPanelRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            });
        }
    }

    return (
        <section>
            <h2 className="text-base font-semibold">
                Get the most out of chat
            </h2>
            <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
                <div className="bg-card divide-y self-start rounded-xl border shadow-sm">
                    {LEARN_TASKS.map((task) => {
                        const isActive = task.id === activeTaskId;
                        return (
                            <div
                                key={task.id}
                                className={cn(isActive && 'bg-muted/70')}
                            >
                                <button
                                    type="button"
                                    aria-expanded={isActive}
                                    aria-controls={`learn-task-panel-${task.id}`}
                                    onClick={() => selectTask(task.id)}
                                    className={cn(
                                        'flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left text-sm transition-colors',
                                        isActive
                                            ? 'text-foreground font-medium'
                                            : 'text-muted-foreground hover:bg-muted/50',
                                    )}
                                >
                                    {task.title}
                                    <ChevronDown
                                        aria-hidden="true"
                                        className={cn(
                                            'text-muted-foreground size-4 shrink-0 transition-transform',
                                            isActive && 'rotate-180',
                                        )}
                                    />
                                </button>
                                <div id={`learn-task-panel-${task.id}`}>
                                    {isActive ? (
                                        <div className="learn-phoebe-turn-in px-4 pb-4">
                                            <p className="text-muted-foreground text-sm leading-6">
                                                {task.tip}
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div
                    ref={chatPanelRef}
                    className="min-w-0 lg:sticky lg:top-2 lg:self-start"
                >
                    {/* Keyed so switching or replaying a task remounts the chat
                        and every playback hook restarts from zero — no
                        cross-task stale state. */}
                    <FakeChat
                        key={`${activeTaskId ?? 'idle'}-${playbackRun}`}
                        task={activeTask}
                    />
                    {activeTask !== null ? (
                        // Both ways to take the prompt live together: run it
                        // here, or copy it and type it yourself.
                        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                            {canTryInChat ? (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        captureTryInChatClicked({
                                            surface: 'page',
                                            taskId: activeTask.id,
                                            stepId: null,
                                        });
                                        openChatWithComposerText(
                                            promptPlainText(activeTask.prompt),
                                            'learn_phoebe',
                                        );
                                    }}
                                    className="h-7 px-2.5 text-xs"
                                >
                                    Try in chat
                                </Button>
                            ) : null}
                            <CopyPromptAction
                                prompt={activeTask.prompt}
                                surface="page"
                                taskId={activeTask.id}
                                stepId={null}
                                className="text-xs [&_svg]:size-3.5"
                            />
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function FaqSection() {
    // One answer at a time, like the task list above: opening a question
    // closes the previous one instead of stacking walls of text.
    const [openQuestion, setOpenQuestion] = useState<string | null>(null);
    return (
        <section>
            <div className="flex items-baseline justify-between gap-3">
                <h2 className="text-base font-semibold">Quick answers</h2>
                <a
                    href={PRODUCT_DOCS_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 text-sm transition-colors"
                >
                    Phoebe Docs
                    <ArrowUpRight aria-hidden="true" className="size-3.5" />
                </a>
            </div>
            <div className="bg-card mt-3 divide-y rounded-xl border shadow-sm">
                {FAQS.map((faq) => (
                    <Collapsible
                        key={faq.question}
                        open={openQuestion === faq.question}
                        onOpenChange={(isOpen) => {
                            setOpenQuestion(isOpen ? faq.question : null);
                        }}
                    >
                        <CollapsibleTrigger className="group hover:bg-muted/50 flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium">
                            {faq.question}
                            <ChevronDown
                                aria-hidden="true"
                                className="text-muted-foreground size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180"
                            />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="text-muted-foreground px-4 pb-3 text-sm leading-6">
                            {faq.answer}
                        </CollapsibleContent>
                    </Collapsible>
                ))}
            </div>
        </section>
    );
}
